import { cn } from '@/lib/utils';
import { TextArea } from '@pms-digital/design-system';
import { useLayoutEffect, useRef, useState } from 'react';

export interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Click-to-edit text: static wrapped display until clicked, then an
 * auto-growing textarea until blur. Used by the setup-de-singularité table
 * cells. No tooltip: the cell already shows the whole value.
 */
export function EditableText({ value, onChange, placeholder = '—', className }: EditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `draft` isn't read in the body, but the effect must re-run on every keystroke to remeasure scrollHeight against the newly rendered content.
  useLayoutEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editing, draft]);

  function startEditing() {
    setDraft(value);
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== value) onChange(trimmed);
  }

  if (editing) {
    return (
      <TextArea
        label={<span className="sr-only">Modifier le texte</span>}
        ref={textareaRef}
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
      />
    );
  }

  const display = value.trim().length > 0 ? value : placeholder;
  return (
    <button
      type="button"
      onClick={startEditing}
      className={cn(
        'w-full whitespace-pre-wrap break-words text-left',
        value.trim().length === 0 && 'text-gray-400',
        className,
      )}
    >
      {display}
    </button>
  );
}
